import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import styles from '../assets/grid.module.scss';
import { Paginated } from 'src/services/api-handlers/pagination';
import Grid, { Column } from 'src/components/react-table/Grid';
import textFilter from 'src/components/react-table/filters/text-filter';
import { QueryParams } from 'src/types/grid';
import { CellProps } from 'react-table';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import dropdownFilter from 'src/components/react-table/filters/dropdown-filter';

import { ISeanceReport } from 'src/types/seanceReport';
import user from '../../../../../../services/user-auth';
import UserRole from '../../../../../../enumerables/user-role';
import { numberWithCommas } from 'src/helpers/format';
import { IServiceType } from '../../../../../../types/service-types';
import { ICase } from '../../../../../../types/case';
import CaseEnum from '../../../../../../enumerables/case-enum';
import { getValFromEnum } from '../../../../../../components/redux-form/utils/dropdown-values';
import moment from 'moment/moment';

interface Props {
    serviceTypes?: Paginated<IServiceType>;
    data: Paginated<ISeanceReport>;
    providers: SelectObject[];
    students: SelectObject[];
    getData: (params: QueryParams) => void;
    getDataCsv: (params: QueryParams) => void;
}

const typeEnum = CaseEnum ? CaseEnum.getEnum('caseType') : {};
const caseTypes: SelectObject[] = getValFromEnum(typeEnum);

const getColumns = (providers: SelectObject[], students: SelectObject[], calc: boolean): Column<ISeanceReport>[] => [
    {
        Header: 'Case #',
        accessor: 'caseNumber',
        disableSortBy: false,
        disableFilters: false,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<ISeanceReport>) => {
            return original.caseNumber;
        },
        Filter: textFilter<ISeanceReport>(),
    },
    {
        Header: 'Case Type',
        accessor: 'caseType',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<ICase>) => {
            const valueCaseType = caseTypes.find((it) => it.value === original.caseType);
            return valueCaseType ? valueCaseType.label : '';
        },
        Filter: dropdownFilter<ICase>(caseTypes),
    },
    {
        Header: 'Student',
        accessor: 'studentId',
        disableFilters: calc,
        disableSortBy: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<ISeanceReport>) => {
            return original.studentId ? original.studentFirstName + ' ' + original.studentLastName : null;
        },
        Filter: dropdownFilter<ISeanceReport>(students),
    },
    {
        Header: 'Provider',
        accessor: 'providerId',
        disableFilters: calc,
        disableSortBy: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<ISeanceReport>) => {
            if (original.totalRow) {
                return <b>Total</b>;
            }
            if (user.getRoles().includes(UserRole.ROLE_ADMIN)) {
                return (
                    <Link
                        to={Router.generate(routes.PROVIDER_DETAIL, {
                            id: original.providerId,
                            tab: 'main-info',
                            subId: '',
                        })}
                    >
                        {original.providerId ? original.providerName : null}
                    </Link>
                );
            } else {
                return original.providerName ? original.providerName : null;
            }
        },
        Filter: dropdownFilter<ISeanceReport>(providers),
    },
    {
        Header: 'Supervisor',
        accessor: 'supervisor',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<ISeanceReport>) => {
            return original.supervisor;
        },
        disableFilters: calc,
        disableSortBy: true,
        Filter: dropdownFilter<ISeanceReport>(providers),
    },
    {
        Header: 'Order ID',
        accessor: 'orderId',
        disableSortBy: false,
        disableFilters: false,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<ISeanceReport>) => {
            if ('' !== original.orderId) {
                return (
                    <Link to={Router.generate(routes.ORDER_DETAIL, { id: original.orderId })}>{original.orderId}</Link>
                );
            }
            return '';
        },
        Filter: textFilter<ISeanceReport>(),
    },
    {
        Header: 'Order',
        accessor: 'orderName',
        disableSortBy: true,
        disableFilters: false,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<ISeanceReport>) => {
            return original.orderName ?? '';
        },
        Filter: textFilter<ISeanceReport>(),
    },
    {
        Header: 'Service',
        accessor: 'serviceTypeId',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<ISeanceReport>) => {
            return original.serviceTypeTitle ?? '';
        },
        Filter: textFilter<ISeanceReport>(),
    },
    {
        Header: 'Location',
        accessor: 'serviceLocationTitle',
        disableSortBy: true,
        disableFilters: false,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<ISeanceReport>) => {
            return original.serviceLocationTitle ?? '';
        },
        Filter: textFilter<ISeanceReport>(),
    },

    {
        Header: 'Confirmed, hrs',
        accessor: 'confirmedHours',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<ISeanceReport>) => {
            return original.confirmedHours ? `${numberWithCommas(original.confirmedHours)}` : null;
        },
        Filter: textFilter<ISeanceReport>(),
    },
];

const SeanceReportsGrid: React.FunctionComponent<Props> = ({
    data,
    getData,
    providers,
    students,
    getDataCsv,
    serviceTypes,
}: Props) => {
    //const [columns, setColumns] = useState<any>([]);
    //if (providers.length && !columns.length && students.length && !columns.length) {
    //    setColumns(getColumns(providers, students, calc));
    //}
    const [calc, setCalc] = useState<boolean>(false);
    const columns = useMemo(() => {
        if (providers.length && students.length) {
            return getColumns(providers, students, calc);
        }
        return [];
    }, [providers, students, calc]);

    const filtersInit: any = [];
    filtersInit.push({ id: 'dateFrom', value: moment().subtract(7, 'days').format('YYYY-MM-DD') });
    filtersInit.push({ id: 'dateTo', value: moment().format('YYYY-MM-DD') });

    return (
        <div className={classNames(styles.grid)}>
            <Grid<ISeanceReport>
                filtersInit={filtersInit}
                calc={calc}
                setCalc={setCalc}
                getDataCsv={getDataCsv}
                filterGlobal={true}
                calculateRatio={true}
                columns={columns}
                data={data}
                serviceTypes={serviceTypes}
                getData={getData}
                noPagination={true}
            />
        </div>
    );
};

export default SeanceReportsGrid;
